import { gql } from "@apollo/client";
import { ErrorMessage } from "@hookform/error-message";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import DialogTitle from "components/legacy/Dialog/DialogTitle/DialogTitle";
import Loading from "components/legacy/Loading/Loading";
import {
  CreateCustomLogistic,
  CreateCustomLogisticVariables,
  DeleteCustomLogistic,
  DeleteCustomLogisticVariables,
  OrganizationCustomLogisticGroup,
  UpdateCustomLogistic,
  UpdateCustomLogisticVariables,
} from "generated/schemaTypes";
import Cookies from "js-cookie";
import dynamic from "next/dynamic";
import { Controller, useForm } from "react-hook-form";
import { ORGANIZATION_ID_COOKIE_NAME } from "shared/constants/auth";
import { ResourceCustomTheme } from "styles/config";
import useMutation from "utils/useMutation";

const RichTextEditor = dynamic(
  () => import("components/legacy/RichTextEditor/RichTextEditor"),
  { loading: () => <Loading />, ssr: false }
);

const ORGANIZATION_CUSTOM_LOGISTIC_FRAGMENT = gql`
  fragment OrganizationCustomLogistic on Logistic {
    id
    emoji
    title
    description
  }
`;

export const ORGANIZATION_CUSTOM_LOGISTIC_GROUP_FRAGMENT = gql`
  fragment OrganizationCustomLogisticGroup on LogisticGroup {
    id
    options {
      ...OrganizationCustomLogistic
    }
  }
  ${ORGANIZATION_CUSTOM_LOGISTIC_FRAGMENT}
`;

const CREATE_CUSTOM_LOGISTIC = gql`
  mutation CreateCustomLogistic($input: CreateCustomLogisticMutationInput!) {
    createCustomLogistic(input: $input) {
      success
      code
      message
    }
  }
`;

const UPDATE_CUSTOM_LOGISTIC = gql`
  mutation UpdateCustomLogistic($input: UpdateCustomLogisticMutationInput!) {
    updateCustomLogistic(input: $input) {
      success
      code
      message
      logistic {
        id
        emoji
        title
        description
      }
    }
  }
`;

const DELETE_CUSTOM_LOGISTIC = gql`
  mutation DeleteCustomLogistic($input: DeleteCustomLogisticMutationInput!) {
    deleteCustomLogistic(input: $input) {
      success
      code
      message
    }
  }
`;

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {},
  field: {
    marginTop: theme.spacing(1),
  },
}));

interface EditCustomLogisticForm {
  emoji: string;
  title: string;
  description: string;
}

type EditCustomLogisticModalProps = {
  open: boolean;
  onClose(): void;
  onExited(): void;
  logisticGroup?: OrganizationCustomLogisticGroup;
};

function EditCustomLogisticModal({
  open,
  logisticGroup,
  onClose,
  onExited,
}: EditCustomLogisticModalProps) {
  const classes = useStyles();
  const organizationId = Cookies.get(ORGANIZATION_ID_COOKIE_NAME);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditCustomLogisticForm>({
    defaultValues: {
      emoji: logisticGroup?.options[0]?.emoji ?? "",
      title: logisticGroup?.options[0]?.title ?? "",
      description: logisticGroup?.options[0]?.description ?? "",
    },
  });

  const [createLogistic, { loading: creating }] = useMutation<
    CreateCustomLogistic,
    CreateCustomLogisticVariables
  >(CREATE_CUSTOM_LOGISTIC, {
    refetchQueries: ["GlobalOrganizationContentForCompanySettings"],
  });
  const [updateLogistic, { loading: updating }] = useMutation<
    UpdateCustomLogistic,
    UpdateCustomLogisticVariables
  >(UPDATE_CUSTOM_LOGISTIC);
  const [deleteLogistic, { loading: deleting }] = useMutation<
    DeleteCustomLogistic,
    DeleteCustomLogisticVariables
  >(DELETE_CUSTOM_LOGISTIC, {
    refetchQueries: ["GlobalOrganizationContentForCompanySettings"],
  });

  const onSubmit = async (data: EditCustomLogisticForm) => {
    if (!organizationId) {
      return;
    }
    if (logisticGroup?.id) {
      if (!logisticGroup?.options[0]?.id) {
        // This code path should not be possible
        console.error("Something went wrong, could not find logistic id");
        return;
      }

      const logistic = logisticGroup?.options[0];

      // Update
      await updateLogistic({
        variables: {
          input: {
            logisticId: logistic.id,
            title: data.title,
            emoji: data.emoji,
            description: data.description,
          },
        },
      });
    } else {
      // Create
      await createLogistic({
        variables: {
          input: {
            organizationId,
            emoji: data.emoji,
            title: data.title,
            description: data.description,
          },
        },
      });
    }
    onClose();
  };

  const onDelete = async () => {
    if (!logisticGroup?.id) {
      // This code path should not be possible
      console.error("Something went wrong, could not find logistic id");
      return;
    }

    await deleteLogistic({
      variables: {
        input: {
          logisticGroupId: logisticGroup.id,
        },
      },
    });
    onClose();
  };

  const saving = deleting || updating || creating;

  const { ref: titleRef, ...titleProps } = register("title", {
    required: true,
  });
  const { ref: emojiRef, ...emojiProps } = register("emoji", {
    required: true,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onExited={onExited}
      className={classes.root}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle onClose={onClose}>
          {logisticGroup?.id ? "Update a Logistic" : "Create a Logistic"}
        </DialogTitle>
        <DialogContent>
          <TextField
            className={classes.field}
            {...emojiProps}
            inputRef={emojiRef}
            name="emoji"
            label="Emoji"
            variant="outlined"
            error={!!errors.emoji}
            placeholder="🐶"
            fullWidth
          />
          <Typography variant="subtitle1" color="error">
            <ErrorMessage
              errors={errors}
              name="emoji"
              message="Please add an emoji!"
            />
          </Typography>
          <TextField
            className={classes.field}
            {...titleProps}
            inputRef={titleRef}
            name="title"
            label="Title"
            error={!!errors.title}
            variant="outlined"
            placeholder="Dog Friendly!"
            fullWidth
          />
          <Typography variant="subtitle1" color="error">
            <ErrorMessage
              errors={errors}
              name="title"
              message="Please enter a title"
            />
          </Typography>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <RichTextEditor
                label="Description"
                toolbar="top"
                className={classes.field}
                onChange={field.onChange}
                value={field.value}
                onBlur={field.onBlur}
                toolbarFormats={{ link: true }}
                formats={["link"]}
                trimEmpty
                placeholder="We love your dog, as long as they are friendly ❤️"
                height={240}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          {logisticGroup?.id && (
            <Button
              variant="outlined"
              size="large"
              disabled={saving}
              color="secondary"
              onClick={onDelete}
            >
              Delete
            </Button>
          )}
          <Button
            disabled={saving}
            variant="contained"
            color="primary"
            type="submit"
          >
            {logisticGroup?.id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
EditCustomLogisticModal.fragments = {
  customLogistic: ORGANIZATION_CUSTOM_LOGISTIC_FRAGMENT,
  customLogisticGroup: ORGANIZATION_CUSTOM_LOGISTIC_GROUP_FRAGMENT,
};

export default EditCustomLogisticModal;
